import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { gsap, ScrollTrigger, scrollToTarget } from "@/lib/lenis";

const FRAMES = [
  "/assets/hero/frame-1.jpg",
  "/assets/hero/frame-2.jpg",
  "/assets/hero/frame-3.jpg",
  "/assets/hero/frame-4.jpg",
  "/assets/hero/frame-5.jpg",
];

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const sectionRef = useRef(null);
  const frameRefs = useRef([]);
  const introRef = useRef(null);
  const outroRef = useRef(null);
  const cueRef = useRef(null);
  const progressRef = useRef(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setReduced(rm);

    FRAMES.slice(1).forEach((src) => {
      const img = new Image();
      img.src = src;
    });

    if (rm) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "+=340%",
          scrub: 0.6,
          pin: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            if (progressRef.current) {
              progressRef.current.style.transform = `scaleX(${self.progress})`;
            }
            if (self.progress > 0.94 && !sectionRef.current.dataset.lit) {
              sectionRef.current.dataset.lit = "1";
              window.dispatchEvent(new CustomEvent("fairlume:lit"));
            }
            if (self.progress < 0.5) delete sectionRef.current.dataset.lit;
          },
        },
      });

      tl.to(cueRef.current, { opacity: 0, duration: 0.06 }, 0.04);

      // The problem first: a dark, closed course — light arrives as you scroll
      tl.to(".hero-night-shade", { opacity: 0.42, duration: 0.2, ease: "none" }, 0.22);
      tl.to(".hero-night-shade", { opacity: 0.25, duration: 0.2, ease: "none" }, 0.52);
      tl.to(".hero-night-shade", { opacity: 0.1, duration: 0.18, ease: "none" }, 0.78);

      // Intro headline + scrim out
      tl.to([introRef.current, ".intro-scrim"], { opacity: 0, y: (i, el) => (el === introRef.current ? -60 : 0), duration: 0.14, ease: "power2.in" }, 0.30);

      // Frame crossfades — short blends so the scene never ghosts
      tl.to(frameRefs.current[1], { opacity: 1, duration: 0.13, ease: "power1.inOut" }, 0.20);
      tl.to(frameRefs.current[2], { opacity: 1, duration: 0.13, ease: "power1.inOut" }, 0.40);
      tl.to(frameRefs.current[3], { opacity: 1, duration: 0.13, ease: "power1.inOut" }, 0.60);
      tl.to(frameRefs.current[4], { opacity: 1, duration: 0.15, ease: "power1.inOut" }, 0.78);

      tl.fromTo(
        outroRef.current,
        { opacity: 0, y: 70 },
        { opacity: 1, y: 0, duration: 0.14, ease: "power2.out" },
        0.84
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} data-testid="hero-section" className="relative h-screen overflow-hidden">
      {/* Image sequence */}
      <div className="absolute inset-0">
        {FRAMES.map((src, i) => (
          <img
            key={src}
            ref={(el) => (frameRefs.current[i] = el)}
            src={src}
            alt={i === 0 ? "Dark golf course at dusk with lighting poles switched off" : ""}
            aria-hidden={i !== 0}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: i === 0 || reduced ? (reduced && i < FRAMES.length - 1 ? 0 : 1) : 0 }}
            loading={i === 0 ? "eager" : "lazy"}
            draggable={false}
          />
        ))}
        {/* Progressive night darkening — multiply blend deepens without haze */}
        <div
          className="hero-night-shade absolute inset-0 bg-[#1a2636] mix-blend-multiply pointer-events-none"
          style={{ opacity: reduced ? 0.15 : 0.5 }}
        />
        {/* Readability scrims */}
        <div className="intro-scrim absolute inset-0 bg-gradient-to-r from-[#0a0d0c]/90 via-[#0a0d0c]/45 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#101214]/80 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#101214] to-transparent" />
      </div>

      {/* Intro copy */}
      <div
        ref={introRef}
        className="relative z-10 h-full flex flex-col justify-center px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto"
        style={reduced ? { display: "none" } : undefined}
      >
        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FFC857] mb-8 overflow-hidden text-shadow-hero">
          <motion.span className="inline-block" variants={lineReveal} custom={0} initial="hidden" animate="show">
            Commercial Golf Course Lighting
          </motion.span>
        </p>
        <h1 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl text-shadow-hero">
          <span className="block overflow-hidden">
            <motion.span className="block" variants={lineReveal} custom={1} initial="hidden" animate="show">
              When the Sun Sets,
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span className="block text-[#A7D7A0]" variants={lineReveal} custom={2} initial="hidden" animate="show">
              Opportunity Rises.
            </motion.span>
          </span>
        </h1>
        <div className="overflow-hidden mt-8">
          <motion.p
            className="text-base sm:text-lg text-white/85 max-w-xl leading-relaxed text-shadow-hero"
            variants={lineReveal}
            custom={3}
            initial="hidden"
            animate="show"
          >
            Transform your course into a destination that doesn't close at sunset.
          </motion.p>
        </div>
      </div>

      {/* Outro copy */}
      <div
        ref={outroRef}
        className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6"
        style={reduced ? { opacity: 1 } : { opacity: 0 }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(4,7,10,0.55)_0%,transparent_65%)] pointer-events-none" />
        <p className="relative font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FFC857] mb-6 text-shadow-hero">
          Fairlume Night Golf
        </p>
        <h2 className="relative font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-shadow-hero">
          Your Course.<br />
          <span className="text-[#A7D7A0]">Beyond Daylight.</span>
        </h2>
        <button
          data-testid="hero-illuminate-btn"
          onClick={() => scrollToTarget("#quote")}
          className="relative mt-10 inline-flex items-center gap-3 bg-[#A7D7A0] text-[#101214] font-semibold px-8 py-4 rounded-sm text-base hover:bg-white transition-colors duration-300 group"
        >
          Illuminate Your Course
          <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </button>
      </div>

      {/* Scroll cue */}
      {!reduced && (
        <div ref={cueRef} className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/70 text-shadow-hero">
            Scroll to Illuminate
          </span>
          <div className="w-px h-12 bg-white/20 overflow-hidden">
            <div className="w-full h-full bg-[#A7D7A0] scroll-cue-line" />
          </div>
          <ArrowDown size={14} className="text-white/50" />
        </div>
      )}

      {/* Scroll progress */}
      {!reduced && (
        <div className="absolute bottom-0 inset-x-0 h-px bg-white/10 z-20">
          <div ref={progressRef} className="h-full bg-[#FFC857] origin-left scale-x-0" />
        </div>
      )}
    </section>
  );
}
